import { CircleAlert, CircleCheck, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Toaster as SonnerToaster, toast } from 'sonner';

import { cn } from '../lib/cn';
import { Icon } from './icon';

/**
 * 토스트 — Sonner 래퍼(D144 ① · D3 §4.6). `sonner`를 아는 파일은 이것 하나다.
 *
 * 화면 밖이나 다른 자리에 생긴 결과(보냄 · 복사함 · 내보냄)를 알린다.
 * 그 자리에 남아야 하는 상태는 `StatusText`, 요청 실패로 화면이 비면 `InlineError`·`ErrorCard`다.
 * 토스트에만 있는 정보를 만들지 않는다 — 사라지는 것이 전제다.
 */

export const TOAST_DURATION_MS = 4000;
/** 행동이 있거나 실패를 알리는 토스트 — Sonner는 Esc로 닫지 않으므로 닫기 버튼이 키보드의 유일한 길이다. */
export const TOAST_LONG_DURATION_MS = 8000;
export const TOAST_VISIBLE_MAX = 3;

const TOAST_CLASS = cn(
  'border-line bg-surface-raised shadow-pop rounded-xl border',
  'gap-snug px-card py-row flex w-[var(--width)] items-start',
  'text-ink font-sans text-sm',
);

export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-right"
      visibleToasts={TOAST_VISIBLE_MAX}
      duration={TOAST_DURATION_MS}
      gap={14}
      theme="light"
      richColors={false}
      containerAriaLabel="알림"
      /* Sonner 기본 z-index(999999999)를 층 토큰으로 내린다 — 주입된 스타일보다 앞서야 해서 !를 쓴다. */
      className="!z-toast"
      icons={{
        success: <Icon as={CircleCheck} size="md" className="text-success" />,
        error: <Icon as={CircleAlert} size="md" className="text-danger" />,
        close: <Icon as={X} size="sm" />,
      }}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: '알림 닫기',
        classNames: {
          toast: TOAST_CLASS,
          content: 'gap-tight flex min-w-0 flex-1 flex-col',
          title: 'text-fg font-medium',
          description: 'text-ink-muted text-xs',
          icon: 'mt-px flex shrink-0',
          actionButton: cn(
            'text-action px-snug py-tight -my-tight shrink-0 rounded-md text-sm font-medium',
            'hover:bg-highlight ease-flow duration-fast transition-colors',
          ),
          closeButton: cn(
            'text-ink-muted hover:text-ink hover:bg-highlight p-tight -m-tight order-last shrink-0 rounded-md',
            'ease-flow duration-fast transition-colors',
          ),
        },
      }}
    />
  );
}

export interface NotifyOptions {
  /** 제목 아래 한 줄. */
  description?: ReactNode;
  /** 되돌리기 등 하나의 행동. 있으면 닫기 버튼과 8초가 붙는다. */
  action?: { label: string; onClick: () => void };
}

function options({ description, action }: NotifyOptions = {}) {
  const long = action !== undefined;
  return {
    description,
    action,
    closeButton: long,
    duration: long ? TOAST_LONG_DURATION_MS : TOAST_DURATION_MS,
  };
}

/**
 * 방금 한 동작의 결과를 알린다. 앱은 `sonner`의 `toast` 대신 이것만 부른다.
 * 실패도 `polite`다 — 바로 끼어들어야 하는 실패는 그 자리의 `InlineError`가 맡는다.
 */
export const notify = Object.assign(
  (title: ReactNode, opts?: NotifyOptions) => toast(title, options(opts)),
  {
    success: (title: ReactNode, opts?: NotifyOptions) => toast.success(title, options(opts)),
    error: (title: ReactNode, opts?: NotifyOptions) =>
      toast.error(title, {
        ...options(opts),
        closeButton: true,
        duration: TOAST_LONG_DURATION_MS,
      }),
    dismiss: (id?: string | number) => toast.dismiss(id),
  },
);
